import styled from '@emotion/styled'
import { Form, Formik, FormikHelpers } from 'formik'
import gql from 'graphql-tag'
import React from 'react'
import toastr from 'toastr'
import { useMutation } from '../../../../api/graphql'
import { inputFieldHeight } from '../../../../styles/vars'
import Button from '../../form-fields/Button'
import StringInput from '../../form-fields/StringInput'

// Matches Devise's `config.password_length` minimum (server is source of truth).
const MIN_PASSWORD_LENGTH = 6

const PASSWORD_UPDATE = gql`
  mutation PasswordUpdate($input: PasswordUpdateInput!) {
    passwordUpdate(input: $input) {
      success
      errors {
        attribute
        message
      }
    }
  }
`

const SEND_RESET_PASSWORD_INSTRUCTIONS = gql`
  mutation SendResetPasswordInstructions($input: SendResetPasswordInstructionsInput!) {
    sendResetPasswordInstructions(input: $input) {
      success
      errors {
        attribute
        message
      }
    }
  }
`

interface PasswordEditProps {
  email: string
}

interface PasswordFormValues {
  currentPassword: string
  newPassword: string
  newPasswordConfirmation: string
}

const initialValues: PasswordFormValues = {
  currentPassword: '',
  newPassword: '',
  newPasswordConfirmation: '',
}

const validate = (values: PasswordFormValues) => {
  const errors: Partial<Record<keyof PasswordFormValues, string>> = {}
  if (!values.currentPassword) errors.currentPassword = 'Current password is required'
  if (!values.newPassword) {
    errors.newPassword = 'New password is required'
  } else if (values.newPassword.length < MIN_PASSWORD_LENGTH) {
    errors.newPassword = `Must be at least ${MIN_PASSWORD_LENGTH} characters`
  }
  if (!values.newPasswordConfirmation) {
    errors.newPasswordConfirmation = 'Please confirm the new password'
  } else if (values.newPassword && values.newPassword !== values.newPasswordConfirmation) {
    errors.newPasswordConfirmation = "Passwords don't match"
  }
  return errors
}

const PasswordEdit: React.FC<PasswordEditProps> = ({ email }) => {
  const [updatePassword, { loading: updating }] = useMutation(PASSWORD_UPDATE)
  const [sendReset, { loading: sendingReset }] = useMutation(SEND_RESET_PASSWORD_INSTRUCTIONS)

  const handleSubmit = async (values: PasswordFormValues, helpers: FormikHelpers<PasswordFormValues>) => {
    try {
      const { data } = await updatePassword({ variables: { input: values } })
      const result = data?.passwordUpdate
      if (result?.success) {
        toastr.success('Password updated.')
        helpers.resetForm()
      } else {
        const fieldErrors = result?.errors ?? []
        fieldErrors.forEach(({ attribute, message }: { attribute: string | null; message: string }) => {
          if (attribute && attribute in values) {
            helpers.setFieldError(attribute, message)
          } else {
            toastr.error(message)
          }
        })
      }
    } catch (error) {
      console.error(error)
      window.Rollbar?.error(error)
      toastr.error("Couldn't update your password. Please try again.")
    }
  }

  const handleSendReset = async () => {
    try {
      const { data } = await sendReset({ variables: { input: { email } } })
      if (data?.sendResetPasswordInstructions?.success) {
        toastr.success(`Reset link sent to ${email}. Check your inbox.`)
      } else {
        toastr.error("Couldn't send a reset link. Please try again.")
      }
    } catch (error) {
      console.error(error)
      window.Rollbar?.error(error)
      toastr.error("Couldn't send a reset link. Please try again.")
    }
  }

  return (
    <StyledSection>
      <StyledHeader>
        <h2>Password</h2>
      </StyledHeader>

      <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
        <Form>
          <StringInput name="currentPassword" type="password" placeholder="Current password" />
          <StringInput name="newPassword" type="password" placeholder={`New password (min ${MIN_PASSWORD_LENGTH} characters)`} />
          <StringInput name="newPasswordConfirmation" type="password" placeholder="Confirm new password" />
          <StyledSubmit type="submit" disabled={updating || sendingReset} shouldShowLoadingSpinner={updating} text="Update password" />
        </Form>
      </Formik>

      <StyledResetLink type="button" onClick={handleSendReset} disabled={updating || sendingReset}>
        Forgot your current password? Send me a reset link.
      </StyledResetLink>
    </StyledSection>
  )
}

export default PasswordEdit

const StyledSection = styled.section`
  margin-top: 60px;
  padding-top: 40px;
  border-top: 1px solid #e5e5e5;
`

const StyledHeader = styled.div`
  margin-bottom: 24px;
  color: #102638;
`

const StyledSubmit = styled(Button)`
  display: inline-block;
  background-color: #6fbf53;
  color: white;
  width: 200px;
  height: ${inputFieldHeight};
  margin-top: 24px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 4px;
  padding: 0.625em 1.75em;
  text-align: center;
  cursor: pointer;
`

const StyledResetLink = styled.button`
  display: inline-block;
  margin-top: 24px;
  background: none;
  border: none;
  padding: 0;
  color: #4a90e2;
  font-size: 14px;
  cursor: pointer;
  text-decoration: underline;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
